<script setup lang="ts">
import { IdvDocuments, type IdvDocumentDisplay } from '@Constants/idvDocumentTypeDisplay.constants';
import { type PropType } from 'vue';
import InstructionsCarouselSlide from './InstructionsCarouselSlide.vue';

import IdFront from '@Assets/images/idv/be_prepared_DL_Other.svg'
import DlBarcode from '@Assets/images/idv/scan_barcode_DL.svg'
import OtherBarcode from '@Assets/images/idv/capture_back_Other.svg'
import PassportFront from '@Assets/images/idv/be_prepared_Passport.svg'

const props = defineProps({
    docType: Object as PropType<IdvDocumentDisplay>
})

const emit = defineEmits(['slide-changed'])

const carouselRefs: { [key: string]: any } = {}

const next = function() {
    let carousel = carouselRefs['carousel_'+props.docType?.value!];
    carousel?.next();
}

const isOnLastSlide = function() {
    let carousel = carouselRefs['carousel_'+props.docType?.value!];
    return carousel?.isOnLastSlide();
}

const reset = function() {
    let carousel = carouselRefs['carousel_'+props.docType?.value!];
    carousel?.goTo(0);
}

defineExpose({ next, isOnLastSlide, reset })

const onSlideChanged = () => { emit('slide-changed') }

const slideMap: { [key: string]: any[] } = {
                [IdvDocuments.NorthAmericanDriversLicense.value]: [
                    {
                        image: IdFront,
                        imageAlt: "ID card on a dark background",
                        title: "Be prepared",
                        description: "Find a dark, flat surface to lay your ID on for an easy automatic capture."
                    },
                    {
                        image: DlBarcode,
                        imageAlt: "Scanning the barcode on the back of the ID",
                        title: "Scan the barcode",
                        description: "After capturing the front, flip your ID over and focus your camera on the barcode."
                    }
                ],
                [IdvDocuments.Passport.value]: [
                    {
                        image: PassportFront,
                        imageAlt: "Passport on a dark background",
                        title: "Be prepared",
                        description: "Find a dark surface to lay your passport on for an easy automatic capture."
                    },
                ],
                [IdvDocuments.Other.value]: [
                    {
                        image: IdFront,
                        imageAlt: "ID card on a dark background",
                        title: "Be prepared",
                        description: "Find a dark, flat surface to lay your ID on for an easy automatic capture."
                    },
                    {
                        image: OtherBarcode,
                        imageAlt: "Back of an ID card on a dark background",
                        title: "Capture the back",
                        description: "After scanning the front, flip your ID over to capture the backside."
                    }
                ],
                [IdvDocuments.UsPassportCard.value]: [
                    {
                        image: IdFront,
                        imageAlt: "ID card on a dark background",
                        title: "Be prepared",
                        description: "Find a dark, flat surface to lay your ID on for an easy automatic capture."
                    },
                    {
                        image: OtherBarcode,
                        imageAlt: "Back of an ID card on a dark background",
                        title: "Capture the back",
                        description: "After scanning the front, flip your ID over to capture the backside."
                    }
                ]
            }
  
</script>

<template>
    <div>
        <template v-for="(slideGroup, groupName) in slideMap">
            <app-carousel
                :ref="(el: any) => {if (el) carouselRefs[`carousel_${groupName}`] = el}"
                class="instructions-carousel"
                :class="{ 'instructions-hidden': docType?.value !== groupName, 'instructions-active' : docType?.value === groupName }"
                @slide-number-changed="onSlideChanged"
                v-if="slideGroup.length > 0">
                   <InstructionsCarouselSlide
                     v-for="(slide, index) in slideGroup"
                    :key="`${groupName}-${index}`"
                    :image="slide.image"
                    :slide-number="index + 1"
                    :total-slides="slideGroup.length"
                    :show-slide-number="slideGroup.length > 1"
                    :title="slide.title"
                    :image-alt="slide.imageAlt">
                    {{ slide.description }}
                   </InstructionsCarouselSlide>
                </app-carousel>
        </template>
    </div>
</template>

<style lang="scss" scoped>
@use 'certifid-v2-client-hub-styles' as *;

.instructions-carousel {
    position: absolute;
    width: 100%;
    left: 0;

    &.instructions-active {
        position: relative;
        animation: fadeIn 0.8s ease forwards;
    }

    &.instructions-hidden {
        top: 16px;
        position: absolute;
        animation: fadeOut 0.8s ease forwards;
    }
}

@keyframes fadeIn {
  0% { opacity: 0; }
  100% { opacity: 1; }
}

@keyframes fadeOut {
  0% { opacity: 1; }
  100% { opacity: 0; visibility: hidden; }
}

</style>